import React, { useState } from 'react';
import { FaWalking, FaEye, FaHandHoldingHeart, FaProjectDiagram, FaClock, FaChevronUp, FaMapMarkerAlt } from 'react-icons/fa';

const HowItWorksSection: React.FC = () => {
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowScrollIndicator(target.scrollTop > 20);
  };
  const steps = [
    {
      number: 1,
      title: 'ACTIVE FUN',
      description: 'People & Place Discovery — in real life. Active Fun is what happens when you\'re out and about — not scrolling, not searching — just living. CollectedAF is the app you open when you want the world around you to matter.',
      detailsTitle: 'What Active Fun Means',
      details: [
        'It\'s movement, not feeds. You walk. We detect. No scanning. No awkward intros.',
        'It\'s people, not profiles. See who\'s around you right now — personalities, interests, vibes — before you ever speak.',
        'It\'s places that come alive. Bars, gyms, campuses, events — every location becomes interactive the moment you arrive.',
        'It\'s discovery in the moment. No planning. No swiping. Just real opportunities, revealed as they happen.'
      ],
      icon: FaMapMarkerAlt
    },
    {
      number: 2,
      title: 'Walk past anyone',
      description: 'CollectedAF quietly detects people around you as you move through the world — no scanning, no searching, no awkward icebreakers.',
      details: [
        'If someone is nearby, your phone knows.',
        'If someone interesting walks by, you see it.'
      ],
      icon: FaWalking
    },
    {
      number: 3,
      title: 'See who they are — instantly',
      description: 'Attributes appear with people you pass.',
      hashtags: ['#dogMom', '#MBAStudent', '#KaraokeQueen', '#PiBetaPhi'],
      details: [
        'You see personality before you ever speak.',
        'No guessing. No assumptions.',
        'Just real traits, chosen by real people, made visible in real time.'
      ],
      icon: FaEye
    },
    {
      number: 4,
      title: 'Collect the moment',
      description: 'When you cross paths with someone meaningful, you automatically "collect" them — like a digital keepsake of the moment.',
      details: [
        'No swiping, no profiles, no forced matching.',
        'You met in the real world, and now you\'re connected in the digital one.'
      ],
      icon: FaHandHoldingHeart
    },
    {
      number: 5,
      title: 'Build your network through real life — not feeds',
      description: 'Every person you collect becomes part of your world:',
      details: [
        'Your campus network.',
        'Your bar network.',
        'Your event network.',
        'Your life network.',
        'All built through actual proximity, not algorithmic chance.'
      ],
      icon: FaProjectDiagram
    },
    {
      number: 6,
      title: 'Never miss your moment',
      description: 'Someone important could walk right past you — a friend, a collaborator, even something more.',
      details: [
        'CollectedAF makes sure you see them, in the moment it matters.',
        'Because some moments only happen once.'
      ],
      icon: FaClock
    }
  ];

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        {/* Background Giant C */}
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[30rem] opacity-25 pointer-events-none"
          style={{ 
            fontFamily: '"Anton", sans-serif',
            background: 'linear-gradient(135deg, #fbbf24 0%, #14b8a6 45%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.35))'
          }}
        >
          C
        </div>

        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="colorful-ring bg-BLACK/96 backdrop-blur-2xl rounded-3xl p-6 lg:p-12 w-full h-auto lg:h-full transition-colors shadow-2xl flex flex-col">
            <h2 className="text-4xl lg:text-5xl font-bold text-WHITE mb-3 text-center">The CollectedAF Experience</h2>
            <div className="bg-WHITE/10 backdrop-blur-xl rounded-2xl px-6 py-3 mb-6 mx-auto w-fit border border-WHITE/15 shadow-lg">
              <p
                className="text-xl font-bold text-center leading-relaxed"
                style={{
                  background: 'linear-gradient(90deg, #000000 0%, #0066cc 50%, #000000 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                We turn real-life moments into real connections — instantly.
              </p>
            </div>
            
            {/* Scroll Indicator - Shows when scrolled */}
            <div className={`absolute top-32 left-0 right-0 h-12 bg-gradient-to-b from-WHITE/20 to-transparent pointer-events-none transition-opacity duration-300 z-10 ${showScrollIndicator ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex justify-center pt-2">
                <FaChevronUp className="text-WHITE/60 animate-bounce" size={24} />
              </div>
            </div>
            
            {/* Steps - Scrollable */}
            <div className="flex-1 overflow-y-auto space-y-6 pr-2" onScroll={handleScroll}>
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.number} className="bg-WHITE/5 rounded-xl p-6 border border-WHITE/10 hover:border-WHITE/30 transition-colors">
                    {/* Mobile card - full-width content below icon/number/title */}
                    <div className="lg:hidden flex flex-col gap-4">
                      <div className="flex items-start gap-4 flex-shrink-0">
                        <div className="flex-shrink-0 w-16 h-16 rounded-full bg-WHITE/20 backdrop-blur-md flex items-center justify-center border-2 border-WHITE/25">
                          <Icon className="text-WHITE" size={28} />
                        </div>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-WHITE/20 border border-WHITE/30 flex items-center justify-center flex-shrink-0">
                            <span className="text-WHITE text-lg font-bold">{step.number}</span>
                          </div>
                          <h4 className="text-WHITE text-2xl font-bold">{step.title}</h4>
                        </div>
                      </div>
                      <div className="w-full">
                        <p className="text-WHITE/70 text-base leading-relaxed mb-3">
                          {step.description}
                          {step.hashtags && (
                            <span className="inline-flex flex-wrap gap-2 ml-1">
                              {step.hashtags.map((tag, idx) => (
                                <span key={idx} className="text-WHITE">
                                  <span className="text-WHITE/50">#</span>{tag.substring(1)}
                                  {idx < step.hashtags.length - 1 && ','}
                                </span>
                              ))}
                            </span>
                          )}
                        </p>
                        {step.detailsTitle && (
                          <h5 className="text-WHITE/80 font-bold text-sm mb-2">{step.detailsTitle}</h5>
                        )}
                        {step.details && step.details.length > 0 && (
                          <ul className="space-y-1">
                            {step.details.map((detail, idx) => (
                              <li key={idx} className="text-WHITE/60 text-sm flex items-start gap-2">
                                <span className="text-WHITE/50 mt-1 flex-shrink-0">•</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {/* Desktop card - original layout: icon left, content right */}
                    <div className="hidden lg:flex items-start gap-4">
                      <div className="flex-shrink-0 w-16 h-16 rounded-full bg-WHITE/20 backdrop-blur-md flex items-center justify-center border-2 border-WHITE/25">
                        <Icon className="text-WHITE" size={28} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-8 h-8 rounded-full bg-WHITE/20 border border-WHITE/30 flex items-center justify-center">
                            <span className="text-WHITE text-lg font-bold">{step.number}</span>
                          </div>
                          <h4 className="text-WHITE text-2xl font-bold">{step.title}</h4>
                        </div>
                        <p className="text-WHITE/70 text-base leading-relaxed ml-11 mb-3">
                          {step.description}
                          {step.hashtags && (
                            <span className="inline-flex flex-wrap gap-2 ml-1">
                              {step.hashtags.map((tag, idx) => (
                                <span key={idx} className="text-WHITE">
                                  <span className="text-WHITE/50">#</span>{tag.substring(1)}
                                  {idx < step.hashtags.length - 1 && ','}
                                </span>
                              ))}
                            </span>
                          )}
                        </p>
                        {step.detailsTitle && (
                          <h5 className="text-WHITE/80 font-bold text-sm mb-2 ml-11">{step.detailsTitle}</h5>
                        )}
                        {step.details && step.details.length > 0 && (
                          <ul className="space-y-1 ml-11">
                            {step.details.map((detail, idx) => (
                              <li key={idx} className="text-WHITE/60 text-sm flex items-start gap-2">
                                <span className="text-WHITE/50 mt-1">•</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksSection;
